import { Drawer } from '@mui/material';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { LeihBarPermissions, PermissionEnum } from '@thro-types/access-handler';
import { TermsOfAcceptance } from '@thro-types/leihbar';
import { User } from '@thro-types/users';

import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router';

import {
  useShoppingCartQuery,
  useTermsOfAcceptanceMutation,
  useTermsOfAcceptanceQuery
} from 'api/hooks/leihbar';
import { ErrorBoundary } from 'app/components';
import { NotFoundPage } from 'app/components/NotFoundPage';
import PrivateRoute from 'app/components/Util/PrivateRoute';
import { translations } from 'locales/i18n';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { useInjectReducer } from 'utils/redux-injectors';

import { AlertActions } from '../Dashboard/Alert/slice';
import { Access } from './Access';
import { Articles } from './Articles';
import { Daily } from './Daily';
import { InfoDialogLeihbar } from './InfoDialog';
import { OrderDetailOverview } from './Orders/detail';
import { Overview } from './Overview';
import { Products } from './Products';
import { LenderProfile } from './Profile';
import {
  ShoppingCartContent,
  selectShoppingCart,
  sliceKey,
  reducer,
  ShoppingCartActions
} from './ShoppingCart';
import { Statistics } from './Statistics';
import { areTermsAccepted } from './helper';

interface Props {
  currentUser: User;
  userPermission: PermissionEnum[];
}

export const LeihBar = ({ currentUser, userPermission }: Props) => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  const shoppingCart = useSelector(selectShoppingCart);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const isLeihbarEnabled = useBooleanFlagValue(FEATURE_FLAGS.LEIHBAR.ENABLED, true);

  const { update } = useTermsOfAcceptanceMutation();

  const {
    data: leihbarTermsOfAcceptance,
    isLoading: termsOfAcceptanceLoading,
    isError
  } = useTermsOfAcceptanceQuery();

  const { data, isLoading } = useShoppingCartQuery();

  const confirmTermsAcceptance = async () => {
    await update.mutateAsync();
    setInfoDialogOpen(false);
  };

  const declineTermsAcceptance = () => {
    setInfoDialogOpen(false);
    navigate('/');
  };

  const checkIfLeihbarTermsAccepted = (leihbarTermsOfAcceptance: TermsOfAcceptance | undefined) => {
    if (!leihbarTermsOfAcceptance || !areTermsAccepted(leihbarTermsOfAcceptance)) {
      return setInfoDialogOpen(true);
    }
  };

  // Update shopping cart data when it becomes available
  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      dispatch(ShoppingCartActions.setItems(data));
    }
  }, [data, dispatch, isLoading]);

  // Check terms of acceptance when the data is available
  useEffect(() => {
    if (!termsOfAcceptanceLoading && !isError) {
      checkIfLeihbarTermsAccepted(leihbarTermsOfAcceptance);
    }
  }, [leihbarTermsOfAcceptance, termsOfAcceptanceLoading, isError]);

  useLayoutEffect(() => {
    if (!isLeihbarEnabled) {
      navigate('/');
      dispatch(AlertActions.warning(t(translations.common.areaCurrentlyDisabled())));
    }
  }, [dispatch, isLeihbarEnabled, navigate, t]);

  return (
    <ErrorBoundary boundaryTag="LeihBar">
      <InfoDialogLeihbar
        dialogOpen={infoDialogOpen}
        handleConfirm={confirmTermsAcceptance}
        handleClose={() => setInfoDialogOpen(false)}
        handleDecline={declineTermsAcceptance}
      />
      <Drawer
        anchor="right"
        open={shoppingCart.open}
        onClose={() => dispatch(ShoppingCartActions.close())}
        PaperProps={{
          sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
        }}
      >
        <ShoppingCartContent />
      </Drawer>
      <Routes>
        <Route
          index
          element={
            <PrivateRoute
              user={currentUser}
              userPermissions={userPermission}
              requiredPermission={LeihBarPermissions.LEIHBAR_PRODUCT_READ}
            >
              <Overview />
            </PrivateRoute>
          }
        />
        <Route
          path="/product/*"
          element={<Products currentUser={currentUser} userPermission={userPermission} />}
        />
        <Route
          path="/article/*"
          element={<Articles currentUser={currentUser} userPermission={userPermission} />}
        />
        <Route path="/profile" element={<LenderProfile />} />
        <Route
          path="/orders/:id"
          element={
            <PrivateRoute
              user={currentUser}
              userPermissions={userPermission}
              requiredPermission={LeihBarPermissions.LEIHBAR_ORDER_READ}
            >
              <OrderDetailOverview />
            </PrivateRoute>
          }
        />
        <Route
          path="/daily/*"
          element={<Daily currentUser={currentUser} userPermission={userPermission} />}
        />
        <Route
          path="/statistics"
          element={
            <PrivateRoute
              user={currentUser}
              userPermissions={userPermission}
              requiredPermission={LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_ANY}
            >
              <Statistics />
            </PrivateRoute>
          }
        />
        <Route
          path="/access"
          element={
            <PrivateRoute
              user={currentUser}
              userPermissions={userPermission}
              requiredPermission={LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_ANY}
            >
              <Access />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
};
